import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import {
  closeShift,
  currentShift,
  db,
  openShift,
  tallyShift,
  type Shift,
  type ShiftTally,
} from '../lib/db'
import { clock, elapsedText, money } from '../lib/money'
import { useNow } from '../lib/hooks'
import { useApp } from '../lib/store'
import { pullNow, pushNow } from '../lib/sync'
import { AppBar } from '../components/AppBar'
import { NumPad } from '../components/NumPad'
import { CashCounter } from '../components/CashCounter'

type Step = 'idle' | 'opening' | 'running' | 'counting' | 'done'

export function ShiftScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const now = useNow(30_000)

  const [step, setStep] = useState<Step>('idle')
  const [openingCash, setOpeningCash] = useState(0)
  const [counted, setCounted] = useState(0)
  const [note, setNote] = useState('')
  const [closed, setClosed] = useState<Shift | null>(null)
  const [tally, setTally] = useState<ShiftTally | null>(null)

  const shift = useLiveQuery(() => currentShift(), [])
  const history = useLiveQuery(
    () => db.shifts.filter((s) => s.closedAt !== null).reverse().sortBy('openedAt'),
    [],
    [],
  )

  // Số liệu ca chạy lại mỗi khi có đơn mới hoặc phiếu chi mới, không phải chỉ lúc mở màn hình.
  const orderCount = useLiveQuery(() => db.orders.count(), [], 0)
  const expenseCount = useLiveQuery(() => db.expenses.count(), [], 0)

  useEffect(() => {
    if (!shift) {
      setTally(null)
      return
    }
    void tallyShift(shift).then(setTally)
  }, [shift, orderCount, expenseCount, now])

  useEffect(() => {
    if (step === 'idle') setStep(shift ? 'running' : 'opening')
  }, [shift, step])

  async function doOpen() {
    // Kéo về trước: máy khác trong quán có thể đã mở ca rồi mà máy này chưa biết.
    // Mở thêm ca thứ hai là số tiền phải có trong két tính sai ở cả hai máy.
    await pullNow()
    const already = await currentShift()
    if (already) {
      setStep('running')
      say(`Quán đã mở ca lúc ${clock(already.openedAt)} ở máy khác`)
      return
    }

    await openShift(openingCash)
    setStep('running')
    say(`Đã mở ca với ${money(openingCash)}đ tiền lẻ`)
    void pushNow()
  }

  async function doClose() {
    if (!shift) return
    const result = await closeShift(shift, counted, note || undefined)
    setClosed(result)
    setStep('done')
    void pushNow()
  }

  // ── Chưa mở ca ──
  if (!shift && step !== 'done') {
    return (
      <div className="screen">
        <AppBar title="Mở ca" onBack={() => nav('/')} />
        <div className="body">
          <p className="hint">
            Đếm tiền lẻ đang để sẵn trong két để có cái thối cho khách. Cuối ca app lấy số này
            cộng với tiền bán được, trừ tiền đi chợ, ra đúng số phải có trong ngăn kéo.
          </p>

          <div className="amount-readout num">{openingCash ? money(openingCash) : '0'}</div>

          <div className="chip-row">
            {[200_000, 500_000, 1_000_000, 2_000_000].map((v) => (
              <button
                key={v}
                type="button"
                className="chip"
                aria-pressed={openingCash === v}
                onClick={() => setOpeningCash(v)}
              >
                {money(v)}
              </button>
            ))}
          </div>

          <NumPad value={openingCash} onChange={setOpeningCash} />

          {history.length > 0 && (
            <>
              <h2 className="section-label">Ca đã chốt</h2>
              <div className="list">
                {history.slice(0, 5).map((s) => (
                  <div key={s.id} className="list-row">
                    <span className="list-name">
                      {new Date(s.openedAt).toLocaleDateString('vi-VN')}
                      <small>
                        {clock(s.openedAt)} – {s.closedAt ? clock(s.closedAt) : ''} · bán{' '}
                        {money(s.cashSales)}
                      </small>
                    </span>
                    <span className={`diff-tag ${diffClass(s.cashDiff)}`}>{diffText(s.cashDiff)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="dock">
          <button type="button" className="btn btn-brand btn-lg grow" onClick={doOpen}>
            Mở ca · {money(openingCash)}đ
          </button>
        </div>
      </div>
    )
  }

  // ── Đã chốt xong, hiện kết quả ──
  if (step === 'done' && closed) {
    const over = closed.cashDiff > 0
    const short = closed.cashDiff < 0
    return (
      <div className="screen">
        <div className="body center">
          <div className="done">
            <div className={`done-mark${short ? ' is-short' : ''}`} aria-hidden="true">
              {closed.cashDiff === 0 ? '✓' : short ? '!' : '+'}
            </div>
            <h2>
              {closed.cashDiff === 0
                ? 'Khớp quỹ'
                : short
                  ? `Thiếu ${money(-closed.cashDiff)}đ`
                  : `Thừa ${money(closed.cashDiff)}đ`}
            </h2>
            <p>
              Đúng ra phải có <b className="num">{money(closed.expectedCash)}đ</b>, đếm được{' '}
              <b className="num">{money(closed.countedCash)}đ</b>
            </p>
            {(over || short) && (
              <p className="note center-text">
                Lệch quỹ đã ghi lại kèm giờ chốt ca. Sáng mai dò lại đơn trong ca là ra.
              </p>
            )}
          </div>
        </div>
        <div className="dock">
          <button type="button" className="btn btn-brand btn-lg grow" onClick={() => nav('/')}>
            Xong
          </button>
        </div>
      </div>
    )
  }

  if (!shift || !tally) return <div className="screen" />

  // ── Đang đếm tiền ──
  if (step === 'counting') {
    const diff = counted - tally.expectedCash
    return (
      <div className="screen">
        <AppBar
          title="Đếm tiền trong két"
          sub={`Đúng ra phải có ${money(tally.expectedCash)}đ`}
          onBack={() => setStep('running')}
        />
        <div className="body">
          <p className="hint">
            Chia tiền ra từng cọc rồi bấm số tờ. App tự cộng — khỏi nhẩm.
          </p>

          <CashCounter value={counted} onChange={setCounted} />

          <div className="card">
            <div className="pay-row">
              <span>Đúng ra phải có</span>
              <b className="num">{money(tally.expectedCash)}</b>
            </div>
            <div className="pay-row">
              <span>Đếm được</span>
              <b className="num">{money(counted)}</b>
            </div>
            <div className={`pay-row big divider ${diff < 0 ? 'short' : ''}`}>
              <span>{diff === 0 ? 'Khớp quỹ' : diff < 0 ? 'Thiếu' : 'Thừa'}</span>
              <b className={`num diff-${diffClass(diff)}`}>{money(Math.abs(diff))}</b>
            </div>
          </div>

          <label className="field">
            <span>Ghi chú</span>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Đưa chị Hà 200k mua gas"
              maxLength={250}
            />
          </label>
        </div>

        <div className="dock">
          <button
            type="button"
            className="btn btn-brand btn-lg grow"
            disabled={counted === 0}
            onClick={doClose}
          >
            Chốt ca
          </button>
        </div>
      </div>
    )
  }

  // ── Ca đang chạy ──
  return (
    <div className="screen">
      <AppBar
        title="Ca đang mở"
        sub={`Từ ${clock(shift.openedAt)} · đã ${elapsedText(shift.openedAt, now)}`}
        onBack={() => nav('/')}
      />

      <div className="body">
        <div className="card">
          <div className="pay-row">
            <span>Tiền lẻ đầu ca</span>
            <b className="num">{money(shift.openingCash)}</b>
          </div>
          <div className="pay-row">
            <span>+ Bán thu tiền mặt</span>
            <b className="num">{money(tally.cashSales)}</b>
          </div>
          <div className="pay-row">
            <span>− Chi trả tiền mặt</span>
            <b className="num">{money(tally.cashExpense)}</b>
          </div>
          <div className="pay-row big divider">
            <span>Trong két phải có</span>
            <b className="num">{money(tally.expectedCash)}</b>
          </div>
        </div>

        <div className="card">
          <div className="pay-row">
            <span>Khách chuyển khoản</span>
            <b className="num">{money(tally.transferSales)}</b>
          </div>
          <div className="pay-row">
            <span>Số hoá đơn</span>
            <b className="num">{tally.orderCount}</b>
          </div>
          <p className="note">
            Tiền chuyển khoản không nằm trong két nên không tính vào số trên — nó đã vào thẳng tài
            khoản ngân hàng.
          </p>
        </div>
      </div>

      <div className="dock">
        <button
          type="button"
          className="btn btn-brand btn-lg grow"
          onClick={() => {
            setCounted(0)
            setStep('counting')
          }}
        >
          Đếm tiền &amp; chốt ca
        </button>
      </div>
    </div>
  )
}

const diffClass = (d: number) => (d === 0 ? 'ok' : d < 0 ? 'short' : 'over')

const diffText = (d: number) =>
  d === 0 ? 'Khớp' : d < 0 ? `Thiếu ${money(-d)}` : `Thừa ${money(d)}`
